import { MyProductColumnGroupId } from './myProductColumnGroups'
import { MyOzonProductColumnId, MyProductColumnId, selectColumnId } from './myProductColumnIds'
import { createColumnGroup, createColumns, GroupedMyColumnsParam, MyColumnParam } from './columnGroupCreator'

import { ProductImageCell2 } from '../components/ProductImageCell'
import { TableDateCell } from '../components/tableCells/TableDateCell'
import { TableSelectCell } from '../components/tableCells/TableSelectCell'
import { MyProductOzonSyncCell } from '../components/tableCells/MyProductOzonSyncCell'
import { OzonProductSyncLogsCell } from '../components/tableCells/StoreProductSyncLogsCell'

import { tableCellStyles } from '../hooks/tableStylesHook'

import { emDash } from '../common/stringUtility'
import { formatDateOnly } from '../common/dateTimeUtility'
import { formatBalance, formatPrice, formatPriceOrEmpty } from '../common/numberUtils'

import { MyProductModel } from '../server/mpsklad_core/Models/MyProductModel'
import { OzonProductModel } from '../server/mpsklad_core/Models/OzonProductModel'
import { OzonPrintOrderModel } from '../server/mpsklad_core/Models/OzonPrintOrderModel'

export const myOzonColumns =
  createColumnGroup<MyProductColumnId, MyProductModel>()(
    MyProductColumnGroupId.Ozon, [{
      title: 'Передача остатков',
      id: 'ozonSyncEnabled',
      sortable: true,
      CellComponent: MyProductOzonSyncCell
    }, {
      title: 'Артикул',
      id: 'ozonArticle',
      sortable: true,
      accessor: _ => _.ozonProducts.map(p => p.article)
    }, {
      title: 'Цена',
      id: 'ozonPrice',
      accessor: _ => _.ozonProducts.map(p => formatPriceOrEmpty(p.price)),
      cellStyle: tableCellStyles.noWrap
    }, {
      title: 'Цена без скидки',
      id: 'ozonBasePrice',
      accessor: _ => _.ozonProducts.map(p => formatPriceOrEmpty(p.basePrice)),
      cellStyle: tableCellStyles.noWrap
    }, {
      title: 'Остаток',
      id: 'ozonBalance',
      accessor: _ => _.ozonProducts.map(p => formatBalance(p.balance)),
      cellStyle: tableCellStyles.noWrap
    }, {
      title: 'Выставлен на продажу',
      id: 'ozonIsVisible',
      sortable: true,
      accessor: _ => _.ozonProducts.map(p => p.isVisible),
      cellStyle: _ => _.ozonProducts.some(p => !p.isVisible) ? tableCellStyles.warning : undefined
    }, {
      title: 'В архиве',
      id: 'ozonIsArchived',
      accessor: _ => _.ozonProducts.map(p => p.isArchived == null ? emDash : p.isArchived ? 'Да' : 'Нет'),
      cellStyle: _ => _.ozonProducts.some(p => p.isArchived) ? tableCellStyles.warning : undefined
    }] satisfies GroupedMyColumnsParam<MyOzonProductColumnId, MyProductModel>) // Ensure filtering of ids

export type OzonProductColumnId =
  keyof Pick<OzonProductModel, 'imageUrl' | 'article' | 'barcodes' | 'name' | 'price' | 'basePrice' | 'balance'
    | 'isVisible' | 'isArchived' | 'hasMsProduct'> | 'category' | 'discountPercentage' | 'createdCardDate' | 'cardPublicationStatus' |'apiMessage' |'lastSyncDate'
  | 'logs' | typeof selectColumnId

const ozonProductCommonColumns = [{
  id: 'imageUrl',
  title: 'Фото',
  CellComponent: ProductImageCell2
}, {
  id: 'article',
  title: 'Артикул'
}, {
  id: 'barcodes',
  title: 'Штрихкод',
  cellStyle: tableCellStyles.noWrap
}, {
  id: 'name',
  title: 'Наименование'
}, {
  id: 'price',
  title: 'Цена',
  accessor: _ => formatPriceOrEmpty(_.price),
  cellStyle: tableCellStyles.noWrap
}, {
  id: 'basePrice',
  title: 'Цена без скидки',
  accessor: _ => formatPriceOrEmpty(_.basePrice),
  cellStyle: tableCellStyles.noWrap
}, {
  id: 'balance',
  title: 'Остаток',
  accessor: _ => formatBalance(_.balance),
  cellStyle: tableCellStyles.noWrap
}, {
  id: 'isVisible',
  title: 'Выставлен на продажу',
  cellStyle: _ => !_.isVisible ? tableCellStyles.warning : undefined
}, {
  id: 'isArchived',
  title: 'В архиве',
  accessor: _ => _.isArchived == null ? emDash : _.isArchived ? 'Да' : 'Нет'
}, {
  id: 'hasMsProduct',
  title: 'Сматчен с МоимСкладом'
}] satisfies Array<MyColumnParam<OzonProductColumnId, OzonProductModel>>

export const ozonProductColumns =
  createColumns<OzonProductColumnId, OzonProductModel>(
    ...ozonProductCommonColumns, {
      id: 'logs',
      title: 'Логи',
      CellComponent: OzonProductSyncLogsCell
    })

export const ozonProductImportColumns =
  createColumns<OzonProductColumnId, OzonProductModel>({
    id: selectColumnId,
    title: 'Выбрать',
    CellComponent: TableSelectCell
  }, ...ozonProductCommonColumns)

export type OzonPrintOrderColumnId =
  keyof Pick<OzonPrintOrderModel, 'createdAt' | 'shipmentDate' | 'ozonId' | 'postingNumber' | 'price' | 'productNames'>

// TODO: Allow sorting? Requires backend.
export const ozonPrintOrderColumns =
  createColumns<OzonPrintOrderColumnId, OzonPrintOrderModel>({
    id: 'createdAt',
    title: 'Дата создания',
    CellComponent: TableDateCell
  }, {
    id: 'shipmentDate',
    title: 'Плановая дата отгрузки',
    accessor: _ => {
    if (!_.shipmentDate) return emDash
    const d = new Date(_.shipmentDate)
    return isNaN(d.getTime()) ? emDash : formatDateOnly(d)
  }
  }, {
    id: 'ozonId',
    title: 'Id в Ozon'
  }, {
    id: 'postingNumber',
    title: 'Номер отправления'
  }, {
    id: 'price',
    title: 'Цена продажи, ₽',
    accessor: _ => formatPrice(_.price),
    cellStyle: tableCellStyles.noWrap
  }, {
    id: 'productNames',
    title: 'Наименования товаров',
    accessor: _ => _.productNames.join(', ')
  })